import type { GetStaticPaths, GetStaticProps } from 'next';
import type { SinglePostProps } from 'lib/queries/blog';
import type { FeaturedExpert, Author, Media } from 'types';
import React from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Avatar, Link, Paragraph, Wrap, useToast } from '@leafwell/components';
import ArticleJson from 'components/structured-data/article';
import AuthorInfo from 'components/author-info';
import CardsGrid from 'components/cards-grid';
import WysiwygContent from 'components/wysiwyg-content';
import Figure from 'components/figure';
import HeadingBody from 'components/heading-body';
import Loader from 'components/loader';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { getSinglePost } from 'lib/queries/blog';
import { getDateDay, getDateMonth, getDateYear } from 'lib/utils/dateUtils';

const ContentsTable = dynamic(() => import('components/contents-table'));
const NewsletterForm = dynamic(() => import('components/newsletter-form'), {
  ssr: false,
  loading: () => {
    //to prevent layout shift
    return (
      <Wrap
        id="newsletter-signup"
        size="large"
        className="h-[494px] sm:h-[512px] md:h-[330px] lg:h-[272px]"
      />
    );
  },
});

export const SharePost: React.FC<{
  uri: string;
  title?: string;
}> = ({ uri, title }) => {
  const { translations } = useWordPressContext();
  const { trackEvent } = useEventsTracker();
  const { addToast } = useToast();

  return (
    <div className="mt-6 pt-6 flex gap-x-5 items-center border-t-2 border-t-purple-600">
      <Paragraph
        className="pb-1 font-semibold"
        size="small"
        dangerouslySetInnerHTML={{
          __html: translations['Share this'],
        }}
      />
      <Link
        className="w-12 h-12 flex justify-center	items-center border rounded-full border-lilac-200"
        href={`https://www.facebook.com/sharer/sharer.php?u=${uri}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackEvent({
            event: 'share',
            method: 'facebook',
            content_type: title,
            item_id: 'blog_post',
          })
        }
      >
        <Image
          src="/social/facebook.svg"
          alt="facebook"
          className="inline-block hover:opacity-20 transition duration-150 m-5"
          width={50}
          height={50}
        />
      </Link>
      <Link
        href={`https://twitter.com/intent/tweet?url=${uri}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-12	h-12	flex justify-center	items-center border rounded-full border-lilac-200"
        onClick={() =>
          trackEvent({
            event: 'share',
            method: 'twitter',
            content_type: title,
            item_id: 'blog_post',
          })
        }
      >
        <Image
          src="/social/twitter.svg"
          alt="twitter"
          className="inline-block hover:opacity-20 transition duration-150 m-5"
          width={20}
          height={16}
        />
      </Link>
      <Link
        className="w-12	h-12	flex justify-center	items-center border rounded-full border-lilac-200"
        onClick={async () => {
          addToast({
            content: 'Link copied to clipboard',
            key: 'link-copied',
            type: 'positive',
            duration: 2000,
            shouldRemove: true,
          });
          if ('clipboard' in navigator) {
            trackEvent({
              event: 'share',
              method: 'link',
              content_type: title,
              item_id: 'blog_post',
            });
            return await navigator.clipboard.writeText(uri);
          } else {
            trackEvent({
              event: 'share',
              method: 'link',
              content_type: title,
              item_id: 'blog_post',
            });
            return document.execCommand('copy', true, uri);
          }
        }}
      >
        <Image
          src="/link.svg"
          alt="copy url"
          className="inline-block hover:opacity-20 transition duration-150 m-5"
          width={14}
          height={16}
        />
      </Link>
    </div>
  );
};

type Role = 'reviewer' | 'writer' | 'editor';

const ROLE_TEXT: Record<Role, string> = {
  reviewer: 'Reviewed by',
  writer: 'Written by',
  editor: 'Edited by',
};

const PersonLink: React.FC<{ person: Author }> = ({ person }) => (
  <Link className="text-lilac-500 hover:underline" href={person.uri}>
    {person.displayName || person.name}
  </Link>
);

const joinWithAnd = (nodes: React.ReactNode[]) => {
  // "A and B" or "A, B and C" (though here we’ll only ever have max 2 per role)
  return nodes.map((n, i) => {
    if (i === 0) return n;
    if (i === nodes.length - 1) return <> and {n}</>;
    return <>, {n}</>;
  });
};

export const PostContributors: React.FC<{
  postAuthor: Author[];
  reviewer?: Author | null;
  editedBy?: Author | null;
  displayEditor: boolean;
  displayContentReviewer: boolean;
  displayAuthor: boolean;
  createdAt?: string | null;
  modifiedAt?: string | null;
}> = ({
  postAuthor,
  reviewer,
  editedBy,
  displayEditor,
  displayContentReviewer,
  displayAuthor,
  createdAt,
  modifiedAt,
}) => {
  // 1) Build the candidate list honoring the display flags FIRST
  // Priority order of ROLES: reviewer > writer(s) > editor
  const candidates: { role: Role; person: Author }[] = [];

  if (displayContentReviewer && reviewer) {
    candidates.push({ role: 'reviewer', person: reviewer });
  }

  if (displayAuthor && postAuthor?.length) {
    for (const a of postAuthor) {
      candidates.push({ role: 'writer', person: a });
    }
  }

  if (displayEditor && editedBy) {
    candidates.push({ role: 'editor', person: editedBy });
  }

  // 2) Take up to TWO unique people following the above order
  const seen = new Set<string>();
  const selected: { role: Role; person: Author }[] = [];
  for (const c of candidates) {
    if (seen.has(c.person.id)) continue;
    selected.push(c);
    seen.add(c.person.id);
    if (selected.length === 2) break;
  }

  // 3) Collect avatars (one per selected person, in order)
  const profilePictures = selected
    .map(s => s.person.image || true)
    .filter(Boolean) as Media[];

  // 4) Build the label by grouping the selected people by role
  //    (e.g., "Written by A and B" OR "Reviewed by A | Written by B")
  const byRole: Record<Role, Author[]> = {
    reviewer: [],
    writer: [],
    editor: [],
  };
  for (const s of selected) byRole[s.role].push(s.person);

  const roleChunks: React.ReactNode[] = [];

  (['reviewer', 'writer', 'editor'] as Role[]).forEach(role => {
    if (byRole[role].length) {
      const links = byRole[role].map(p => (
        <PersonLink key={`${role}-${p.id}`} person={p} />
      ));
      roleChunks.push(
        <React.Fragment key={role}>
          {ROLE_TEXT[role]} {joinWithAnd(links)}
        </React.Fragment>,
      );
    }
  });

  const label = roleChunks.reduce<React.ReactNode[]>((acc, chunk, i) => {
    acc.push(chunk);
    if (i < roleChunks.length) acc.push(' | ');
    if (i === roleChunks.length - 1)
      acc.push(
        createdAt === modifiedAt
          ? `Published on ${createdAt}`
          : `Last updated on ${modifiedAt}`,
      );
    return acc;
  }, []);

  return (
    <div className=" flex flex-col md:flex-row items-start gap-4">
      <div className="flex -space-x-2">
        {profilePictures.map((img, i) => (
          <Avatar
            size="large"
            image={img?.src}
            width="48"
            height="48"
            Component={Image}
            className="rounded-full ring-2 ring-white object-cover"
          />
        ))}
      </div>

      <div className="text-base text-gray-700">
        <div className="mb-1">{label}</div>
      </div>
    </div>
  );
};

const SinglePostContent: React.FC<SinglePostProps> = ({
  post: {
    id,
    date,
    modified,
    seo,
    title,
    content,
    featuredMedia,
    postAuthor,
    acf: {
      keyTakeaways,
      blocks,
      relatedArticles,
      reviewer,
      featuredExperts,
      editedBy,
      displayContentReviewer = true,
      displayFeaturedExperts = true,
      displayAuthor = true,
      displayEditor = true,
    },
  },
  tableOfContents,
  readTime,
}) => {
  const { translations } = useWordPressContext();
  const hasAuthor = postAuthor?.length || reviewer || editedBy;
  const hasFeaturedExperts = featuredExperts?.length && displayFeaturedExperts;
  const pageType = 'post';

  const createdAt = `${getDateMonth(date)} ${getDateDay(date)}, ${getDateYear(
    date,
  )}`;
  const modifiedAt = `${getDateMonth(modified)} ${getDateDay(
    modified,
  )}, ${getDateYear(modified)}`;

  return (
    <PageWrap seo={seo} title={title} pageType={pageType}>
      <Wrap
        size="large"
        className="pb-0 lg:grid-cols-12 lg:gap-x-12 xl:grid-cols-16 items-start"
        tagName="article"
      >
        <ArticleJson
          id={id}
          seo={seo}
          date={date}
          title={title}
          modified={modified}
          postAuthor={postAuthor}
        />
        <header className="grid w-full max-w-4xl lg:col-span-full xl:col-start-2 xl:col-span-9 justify-self-center gap-y-10 lg:gap-y-12">
          <div className="max-w-2xl justify-self-center xl:justify-self-start">
            <HeadingBody
              level="2"
              tagName="h1"
              title={title}
              className="font-semibold"
            />
          </div>
          <div>
            <div className="flex justify-between">
              <div className="grid grid-cols-auto-1fr gap-x-2 mb-2">
                <Image
                  src="/education-icon.svg"
                  alt="book-icon"
                  className="mt-1"
                  loading="lazy"
                  width={18}
                  height={18}
                />
                <p className="text-xs">{`${readTime?.minutes} min read`}</p>
              </div>
            </div>
            <Figure
              {...featuredMedia}
              className="aspect-w-16 aspect-h-6"
              objectFit="cover"
              priority={true}
              sizes="(min-width: 1024px) 50vw, 50vw"
            />
          </div>
        </header>
        {(hasAuthor || hasFeaturedExperts || tableOfContents?.length > 0) && (
          <div className="col-span-full xl:top-40 xl:pt-4 xl:col-span-6 xl:order-2 xl:sticky xl:pr-24">
            <Wrap
              size="large"
              spacer={false}
              className="block px-0 lg:!px-0 lg:pl-4 mb-6 md:mb-8 py-0 md:py-0 pb-6 md:pb-8 border-b border-indigo-200"
            >
              <div className="flex justify-between">
                <div className="flex-1 mr-4">
                  <PostContributors
                    postAuthor={postAuthor}
                    reviewer={reviewer}
                    editedBy={editedBy}
                    displayEditor={displayEditor}
                    displayContentReviewer={displayContentReviewer}
                    displayAuthor={displayAuthor}
                    createdAt={createdAt}
                    modifiedAt={modifiedAt}
                  />
                  {hasFeaturedExperts && (
                    <section>
                      <Paragraph className="font-medium pt-4 pb-2" size="small">
                        {translations['Featured experts']}
                      </Paragraph>
                      {featuredExperts.map((author: FeaturedExpert) => {
                        const { expert } = author;
                        return (
                          <AuthorInfo
                            {...expert}
                            className="mb-4"
                            variant="small"
                            key={expert.id}
                          />
                        );
                      })}
                    </section>
                  )}
                </div>
              </div>
            </Wrap>
            {tableOfContents?.length > 0 ? (
              <ContentsTable items={tableOfContents} />
            ) : null}
          </div>
        )}
        <span className="col-span-full xl:col-span-9 xl:order-3 xl:col-start-2">
          {keyTakeaways ? (
            <WysiwygContent
              className="rounded-lg border border-gray-100 p-4 shadow-lg shadow-lilac-500/20 mb-6"
              content={keyTakeaways}
              tagName="section"
            />
          ) : null}
          <WysiwygContent className="" content={content} tagName="section" />
          {blocks.length > 0 ? (
            <span className="order-last leafwell-post-blocks">
              <Blocks blocks={blocks} modified={modified} pageType={pageType} />
            </span>
          ) : null}
          <div className="grid gap-y-6">
            <SharePost uri={seo?.canonical} title={title} />
          </div>
        </span>
      </Wrap>
      {relatedArticles?.length > 0 && (
        <Wrap size="large">
          <CardsGrid
            items={relatedArticles.slice(0, 3)}
            variant="smallFeatured"
            title={translations['Keep Reading']}
          />
        </Wrap>
      )}
      <NewsletterForm filled />
    </PageWrap>
  );
};

const SinglePost: React.FC<SinglePostProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <SinglePostContent {...props} />;
};

export const getStaticProps: GetStaticProps = (async context => {
  return await getSinglePost(context);
}) satisfies GetStaticProps<SinglePostProps>;

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default SinglePost;
